/**
 * Aggregates the monthly triangulation series into quarter / financial-year /
 * calendar-year buckets. Source rows are chronological, so first-seen bucket
 * order stays chronological.
 */
const MONTH_IDX = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11
};

const SUM_KEYS = ["gst", "ledger", "ledgerRepayment", "bank", "bankDebit"];

// Parses a "MMM'YY" / "MMM YYYY" month label into { mi, y }.
function parseLabel(label) {
    if (!label) {
        return null;
    }
    const match = String(label).match(/^([A-Za-z]{3,})'?\s*(\d{2,4})$/);
    if (!match) {
        return null;
    }
    const mi = MONTH_IDX[match[1].slice(0, 3).toLowerCase()];
    if (mi === undefined) {
        return null;
    }
    let y = parseInt(match[2], 10);
    if (y < 100) {
        y += 2000;
    }
    return { mi, y };
}

// Financial year start year (April–March).
function fyStart(mi, y) {
    return mi >= 3 ? y : y - 1;
}

function fyLabel(mi, y) {
    const s = fyStart(mi, y);
    return (
        "FY" +
        String(s % 100).padStart(2, "0") +
        "-" +
        String((s + 1) % 100).padStart(2, "0")
    );
}

function quarterLabel(mi, y) {
    const s = fyStart(mi, y);
    let q = 4;
    if (mi >= 3 && mi <= 5) {
        q = 1;
    } else if (mi >= 6 && mi <= 8) {
        q = 2;
    } else if (mi >= 9 && mi <= 11) {
        q = 3;
    }
    return "Q" + q + " FY" + String((s + 1) % 100).padStart(2, "0");
}

function bucketKey(row, groupBy) {
    const p = parseLabel(row.month);
    if (!p) {
        return row.month;
    }
    if (groupBy === "quarter") {
        return quarterLabel(p.mi, p.y);
    }
    if (groupBy === "fy") {
        return fyLabel(p.mi, p.y);
    }
    if (groupBy === "year") {
        return String(p.y);
    }
    return row.month;
}

export function groupMonthly(monthly, groupBy) {
    if (!groupBy || groupBy === "month") {
        return monthly || [];
    }
    const order = [];
    const map = {};
    (monthly || []).forEach(row => {
        const key = bucketKey(row, groupBy);
        if (!map[key]) {
            map[key] = { month: key, filingDelay: null };
            SUM_KEYS.forEach(k => {
                map[key][k] = 0;
            });
            order.push(key);
        }
        SUM_KEYS.forEach(k => {
            const v = Number(row[k]);
            if (isFinite(v)) {
                map[key][k] += v;
            }
        });
        // Filing delay aggregates as the worst (max) delay in the bucket.
        if (row.filingDelay !== null && row.filingDelay !== undefined) {
            const d = Number(row.filingDelay);
            if (isFinite(d)) {
                map[key].filingDelay =
                    map[key].filingDelay === null
                        ? d
                        : Math.max(map[key].filingDelay, d);
            }
        }
    });
    return order.map(key => {
        const group = map[key];
        SUM_KEYS.forEach(k => {
            group[k] = Math.round(group[k] * 100) / 100;
        });
        return group;
    });
}

export default groupMonthly;
